// Considered: `MessageAtom severity="warn"` — this molecule is BUILT ON it,
// not an alternative to it. `MessageAtom` already accepts a `ReactNode`
// child; what a plain `<MessageAtom>` call site can't do on its own is name
// the conflicting property + its locked origin from `FilterLayer`/i18n and
// fire the `context_filter_conflict_shown` analytics event exactly once per
// conflict — that's the real justification for a molecule here, not the
// visual shell. See spec Reuse Audit (specs/per-level-context-filters.md).
import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import MessageAtom from '../../atoms/Message/MessageAtom';
import type { FilterLayer } from 'types/contextView';
import './FilterConflictNoticeMolecule.scss';

/**
 * The tier as the noun a conflict sentence names ("…conflicts with the context…").
 * Separate from `LAYER_LABEL_KEY`, which is each tier's section heading: a heading is
 * not a noun phrase, and the article belongs to the noun (French genders it).
 */
export const CONFLICT_TIER_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.conflict.tier.context',
  view: 'columnFilters.conflict.tier.view',
  user: 'columnFilters.conflict.tier.user',
  session: 'columnFilters.conflict.tier.session',
  link: 'columnFilters.conflict.tier.link',
};

export const LAYER_LABEL_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.provenance.context.heading',
  view: 'columnFilters.provenance.view.heading',
  user: 'columnFilters.provenance.user.heading',
  session: 'columnFilters.provenance.session.heading',
  link: 'columnFilters.provenance.link.heading',
};

export interface FilterConflictNoticeMoleculeProps {
  /** The property label (already resolved) the draft and locked clauses both constrain. */
  property: string;
  /** Which layer owns the locked clause the draft can't co-exist with. */
  lockedLayer: FilterLayer;
  /**
   * The locked clause's own value (already resolved to a display label) — names
   * WHAT the draft is contradicting, not just that it is (context/column-filter.jsx:484
   * "restricts it to <b>{theirs}</b>"). Omitted → the sentence ends without naming it.
   */
  lockedValue?: string;
  contextSystemName?: string | null;
  levelId?: string;
  className?: string;
}

const FilterConflictNoticeMolecule: React.FC<FilterConflictNoticeMoleculeProps> = ({
  property,
  lockedLayer,
  lockedValue,
  contextSystemName,
  levelId,
  className = '',
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  // Fires once per distinct conflict, not once per render — a draft that
  // keeps changing while still conflicting on the same property must not
  // spam the event.
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${property}:${lockedLayer}`;
    if (firedForRef.current === key) return;
    firedForRef.current = key;
    logAnalyticsEvent('context_filter_conflict_shown', undefined, {
      contextSystemName: contextSystemName ?? null,
      levelId: levelId ?? null,
      property,
      lockedLayer,
    });
  }, [property, lockedLayer, contextSystemName, levelId, logAnalyticsEvent]);

  // Dev note #2: message names the owning tier so users understand which
  // locked constraint they've contradicted and can take action.
  const tierLabel = t(CONFLICT_TIER_KEY[lockedLayer]);

  // The property name (`uf-column.ef-prop-meta-...`) and the locked clause's
  // own value (`uf-column.warn-c-theirs`) are each bolded in the prototype
  // (context/column-filter.jsx:484-485) — `Trans` keeps that emphasis inside
  // an i18n-safe sentence instead of string-concatenating around the
  // interpolated segments, which would hardcode English word order.
  const text = (
    <>
      <Trans
        i18nKey={lockedValue ? 'columnFilters.conflict.messageWithValue' : 'columnFilters.conflict.message'}
        values={{ property, tier: tierLabel, value: lockedValue }}
        components={{
          prop: <strong data-datum="uf-column.ef-prop-meta-warn-c-prop-name-warn-c-pro" data-kind="strong" />,
          val: <strong data-datum="uf-column.warn-c-theirs" data-kind="strong" />,
        }}
      />
      {/* Only the view tier's conflict is actionable from here (dev note:
          context/column-filter.jsx:486) — a locked context clause has no
          "leave it" escape from this popover, so the extra sentence would
          promise an action that doesn't exist. */}
      {lockedLayer === 'view' && <> {t('columnFilters.conflict.viewHint')}</>}
    </>
  );

  return (
    <MessageAtom
      severity="warn"
      className={`filter-conflict-notice ${className}`.trim()}
      text={text}
    />
  );
};

export default FilterConflictNoticeMolecule;
